import {Inject, Injectable} from "@angular/core";
import {HttpBackend, HttpClient, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest} from "@angular/common/http";
import {Router} from "@angular/router";
import {BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError} from "rxjs";
import {select, Store} from "@ngrx/store";
import {AuthState, getToken, LoginSuccess, LogoutUser} from "@frontend/auth-store";
import {LocalstorageService} from "./localstorage.service";
import {APP_CONFIG} from "../index";

@Injectable()
export class HttpAuthInterceptService implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private httpClient: HttpClient;

  constructor(
    private router: Router,
    private authStore: Store<AuthState>,
    private localStorageService: LocalstorageService,
    httpBackend: HttpBackend,
    @Inject(APP_CONFIG) private appConfig: any
  ) {
    // HttpClient created with HttpBackend bypasses all interceptors (prevents infinite loop on refresh call)
    this.httpClient = new HttpClient(httpBackend);
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let newHeaders = new HttpHeaders();
    if (!request.headers.has('Accept')) {
      newHeaders = newHeaders.append('Accept', 'application/json');
    }
    if (!request.headers.has('Content-Type')) {
      newHeaders = newHeaders.append('Content-Type', 'application/json; charset=utf-8');
    }

    const hasTempAuth: boolean = request.headers.has('AddTempAuth');
    if (hasTempAuth) {
      let registerUser = this.localStorageService.updateRegisterUser('get');
      let token = '';
      if (registerUser) {
        token = registerUser.token;
      } else {
        registerUser = this.localStorageService.updateUserKey('get');
        if (registerUser) {
          token = registerUser.token;
        }
      }
      newHeaders = newHeaders.append('Authorization', `Bearer ${token}`);
    }

    const basicAuthRequest = request.clone({headers: newHeaders});
    const hasSkipAuth: boolean = request.headers.has('Skip-auth');

    return this.authStore.pipe(select(getToken)).pipe(
      take(1),
      switchMap((currentToken: string) => {
        const token = currentToken || '';
        if (!hasSkipAuth) {
          return next.handle(basicAuthRequest.clone({
            headers: basicAuthRequest.headers.append('Authorization', `Bearer ${token}`)
          }));
        }
        return next.handle(basicAuthRequest);
      }),
      catchError(error => {
        if (hasSkipAuth) {
          return throwError(error);
        }
        const isUnauthorized = error.status === 401
          || !!(error.statusText && error.statusText.match(/Unauthorized/g))
          || error?.error?.message === 'jwt expired';

        if (isUnauthorized) {
          return this.handle401Error(basicAuthRequest, next);
        }
        return throwError(error);
      }) as any
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const storedUser = this.localStorageService.updateUserKey('get');
    const refreshToken = storedUser?.refreshToken;

    if (!refreshToken) {
      return this.clearAuthAndRedirect();
    }

    if (this.isRefreshing) {
      // Another refresh is already in flight — queue behind it
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => {
          if (!token) {
            // Sentinel value emitted on failure — this refresh cycle failed
            return throwError(() => new Error('Session expired'));
          }
          return next.handle(this.addAuthHeader(request, token));
        })
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.httpClient.post<any>(
      `${this.appConfig.apiUrl}account/refresh-token`,
      {refreshToken},
      {
        headers: new HttpHeaders({
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        })
      }
    ).pipe(
      switchMap((response: any) => {
        this.isRefreshing = false;
        const newToken = response?.data?.token;
        if (newToken) {
          const updatedUser: any = {...storedUser, token: newToken};
          if (response?.data?.refreshToken) {
            updatedUser.refreshToken = response.data.refreshToken;
          }
          this.localStorageService.updateUserKey('store', updatedUser);
          this.authStore.dispatch(LoginSuccess({loginUser: updatedUser, message: ''}));
          this.refreshTokenSubject.next(newToken);
          return next.handle(this.addAuthHeader(request, newToken));
        }
        return this.clearAuthAndRedirect();
      }),
      catchError(() => {
        this.isRefreshing = false;
        // Emit empty string as sentinel to unblock any queued requests, then clear auth
        this.refreshTokenSubject.next('');
        return this.clearAuthAndRedirect();
      })
    );
  }

  private addAuthHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`)
    });
  }

  private clearAuthAndRedirect(): Observable<never> {
    this.isRefreshing = false;
    this.refreshTokenSubject.next(null);
    this.localStorageService.updateRegisterUser('remove');
    this.localStorageService.updateUserKey('remove');
    this.authStore.dispatch(LogoutUser());
    this.router.navigate(['/sign-in']);
    return throwError(() => new Error('Session expired. Please log in again.'));
  }
}
